import {
  Check,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  MessageSquareOff,
  OctagonAlert,
  Minus,
  Trash2,
  X,
} from 'lucide-react';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CHIP_TEXT_CLASS, chipBaseClass, ChipDivider, ChipEvalIcon, chipToneClass } from './eval-chip';
import { registerFieldReveal } from './field-reveal';
import { RichTextEditor } from './rich-text-editor';
import { isFeedbackContentEmpty } from './use-input-block-feedback';

import type { ChipEvaluation } from './eval-chip';
import type { Editor } from '@tiptap/core';

/** Thin vertical divider between an evaluation icon (Check/X/Minus) and its adjacent
 * chip text — stretches to the full chip height so it stays centered regardless of
 * icon size, and separates the icon enough that it never reads as part of the text
 * (e.g. a bare X next to "< 97" misreading as the variable x in an inequality). */
/**
 * The one shape every block's trigger chip uses.
 *
 * `min-w-0` and no `shrink-0`: a chip has to be able to shrink inside its row, or a long label runs
 * over the expand chevron beside it — which is exactly what GPS's "More than 25 m from 40.71283,
 * -74.00601" did. `items-center` puts the check/X/minus in the vertical middle, which is right on one
 * line and is what a two-line chip needs.
 *
 * Blocks used to disagree here: MCQ/Dropdown/Keyword/FITB capped width and truncated on one line,
 * while GPS/Compass/Angle/Numerical had no constraint at all and simply overflowed.
 */
// The chip itself now lives in eval-chip.tsx — this is the trigger chip's flavour of it, and the
// name stays because a dozen blocks import it. The hand-rolled copies that used to sit beside these
// and read heavier than them are gone; there is one chip now.
/**
 * The collapse signal every card in a panel shares.
 *
 * `collapseSignal` was a per-card prop, and exactly ONE card per block was given it — the
 * targeted one. Every other card (No Answer, the catch-all, Default Incorrect) stayed open
 * across a grading-mode switch, which is the half of "cards reopen when I change modes" that
 * survived the first fix. A prop that has to be remembered at fifty call sites will be forgotten
 * at some of them, so the panel states it once for everything inside it.
 *
 * A card may still pass `collapseSignal` explicitly; it wins, for a surface with no panel.
 */
export const FeedbackCollapseContext = createContext<string | undefined>(undefined);

export const TRIGGER_CHIP_CLASS = chipBaseClass({ width: 'auto', grow: true });

export const TRIGGER_CHIP_TEXT_CLASS = CHIP_TEXT_CLASS;

/** Kept as this module's names because most of the app imports them from here. */
export function chipEvalIcon(evaluation: ChipEvaluation) {
  return <ChipEvalIcon evaluation={evaluation} />;
}

export { ChipDivider };

/** Called just before a card's own onDelete() unmounts it — finds the nearest surviving sibling
 * card (via the `data-role='feedback-card'` marker every card root carries) and moves keyboard
 * focus to its first focusable control, so deleting a card never drops a keyboard/screen-reader
 * user's focus all the way back to <body>. Falls back to the shared list container itself when no
 * sibling remains. Generic DOM traversal, not a prop threaded from the caller — this card has no
 * structural knowledge of the list it's rendered inside. */
function focusSiblingFeedbackCardAfterRemoval(fromButton: HTMLElement): void {
  const card = fromButton.closest<HTMLElement>('[data-role="feedback-card"]');
  const list = card?.parentElement;
  if (!card || !list) return;
  const cards = Array.from(list.querySelectorAll<HTMLElement>('[data-role="feedback-card"]'));
  const idx = cards.indexOf(card);
  const sibling = cards[idx + 1] ?? cards[idx - 1] ?? null;
  requestAnimationFrame(() => {
    const target = sibling?.querySelector<HTMLElement>('button, [tabindex]');
    if (target) target.focus();
    else if (!sibling) list.focus();
  });
}

export interface FeedbackCardProps {
  /**
   * The feedback state's id, so an undo can point at THIS card.
   *
   * Feedback is the surface whose changes are otherwise invisible: the editor is showing, the
   * feedback panel is not, and undoing an edit here moved state nobody can see. Every state
   * carries an id in every block that has feedback, which makes this the most precisely
   * targetable surface in the editor — and, before this, the only completely untagged one.
   */
  fieldId?: string;
  title: string;
  content: string;
  evaluation: 'neutral' | 'correct' | 'incorrect';
  /** Prevents title editing and hides the delete button */
  locked?: boolean;
  /** Prevents title editing only — delete stays available. For targeted feedback
   * whose title is mechanically derived from its trigger condition: the title must stay in
   * sync with the trigger, so it's never freely editable, but the state itself is still
   * fully deletable unlike a fixed default (locked) state. */
  titleLocked?: boolean;
  /** Locks the eval button only — prevents cycling even when locked=false */
  evalLocked?: boolean;
  /** Convenience: renders a single colored chip in both the collapsed and expanded trigger zones */
  lockedTriggerLabel?: string;
  /** Custom UI rendered in the expanded card's trigger zone — overrides lockedTriggerLabel */
  triggerZone?: React.ReactNode;
  /** Custom chip(s) rendered in the collapsed card's trigger row — overrides lockedTriggerLabel chip */
  triggerZoneSummary?: React.ReactNode;
  /**
   * Warning-severity message: amber border, triangle icon, a line in the bottom bar.
   *
   * A `duplicateTitle` boolean used to sit beside this, raised whenever two feedback cards derived
   * the same name — which they inevitably do the moment you add a second condition, because the
   * name is DERIVED from a condition that is still blank. So it fired on every new card, told the
   * teacher nothing they could act on, and cleared itself as soon as they typed the condition they
   * were already going to type. The case where two finished conditions really are the same is
   * caught properly by overlap detection, which can say WHICH ones and why.
   */
  warning?: string;
  /**
   * A message at ERROR severity — the same treatment as `warning` one tier up: octagon rather than
   * triangle, error colour rather than amber, and an error-coloured border.
   *
   * The card had only a warning tier, so a state with a genuinely broken condition — a half-filled
   * range, a formula that cannot resolve — was reported by the issues bar as "a trigger somewhere
   * in this block is broken" and by no card at all. The bar names the fault; only the card can name
   * WHICH state has it, and a teacher with eight conditions had nothing to go on.
   *
   * Takes precedence over `warning` in the single collapsed icon slot: a card can hold both, and
   * the more severe one is the one worth the pixel. Both are still listed, in words, on an open
   * card's bottom bar.
   */
  error?: string;
  /** Styles the title as deactivated (muted + italic) — for a fixed default state whose
   * content can never actually be shown (e.g. Correct/Incorrect with no reachable path). */
  inactive?: boolean;
  /** Changes when the card's context does (a grading-mode switch), collapsing it. */
  collapseSignal?: string;
  /** True for the one card the Add button just created — see the `collapsed` initializer. */
  startExpanded?: boolean;
  onTitleChange: (title: string) => void;
  onContentChange: (html: string) => void;
  onEvaluationCycle: () => void;
  onDelete: () => void;
  disabled?: boolean;
  /** When provided (with setActiveEditor), this card registers its editor into a shared,
   * externally-rendered toolbar instead of showing its own on focus — for blocks with a
   * toolbar anchored to the top of the feedback panel (see input-block-frame.tsx's
   * `beforeSlot`), so styling a card far down a long feedback list never means scrolling to
   * find its controls. Omit both to keep this card's own per-focus inline toolbar (the
   * default, still used by every block that hasn't adopted the shared-toolbar pattern). */
  activeEditorRef?: { current: Editor | null };
  setActiveEditor?: (editor: Editor | null) => void;
}

export function FeedbackCard({
  fieldId, title, content, evaluation, locked, titleLocked, evalLocked, lockedTriggerLabel,
  triggerZone, triggerZoneSummary, warning, error, inactive, collapseSignal, startExpanded, onTitleChange,
  onContentChange, onEvaluationCycle, onDelete, disabled,
  activeEditorRef, setActiveEditor,
}: FeedbackCardProps) {
  const { t } = useTranslation();
  const titleRef = useRef<HTMLDivElement>(null);
  const lastTitle = useRef(title);
  const [titleEmpty, setTitleEmpty] = useState(!title);
  const [armed, setArmed] = useState(false);
  const [toolbarActive, setToolbarActive] = useState(false);
  const editorRef = useRef<Editor | null>(null);
  /**
   * Closed, unless the teacher just created this card.
   *
   * It used to open whenever its body was empty, so a panel was reliably half open and half shut
   * and the split tracked nothing the teacher could see — scrolling a block to check its feedback
   * meant reading past however many blank cards happened to exist. "Empty" was standing in for
   * "new", which is a different thing the moment a blank card has been saved and come back.
   *
   * `startExpanded` is that thing said directly: the caller knows which id its Add button just
   * produced, and only that card opens. Read at MOUNT only, so it cannot fight a teacher who
   * closes the card straight away.
   */
  const [collapsed, setCollapsed] = useState(() => !startExpanded);

  /**
   * Collapse everything when the caller says the context changed — a grading-mode switch.
   *
   * These cards do not remount on a switch (their key is the state id), so a card the teacher
   * had collapsed sprang back open: the only thing deciding `collapsed` was the initializer, and
   * an empty-bodied card initialises EXPANDED so it can be typed into. Flipping between modes to
   * compare them meant re-collapsing the list every time.
   *
   * Same stance Flow takes with `defaultCollapsed` — a card is collapsed unless there is a reason
   * to have it open, and "I just added it" is the only such reason.
   */
  const panelSignal = useContext(FeedbackCollapseContext);
  const signal = collapseSignal ?? panelSignal;
  const [lastSignal, setLastSignal] = useState(signal);
  if (lastSignal !== signal) {
    setLastSignal(signal);
    setCollapsed(true);
  }

  /**
   * How an undo opens this card.
   *
   * A collapsed card renders its title and nothing else — no message editor, no trigger — so
   * pulsing it says only "something in here", which is what was reported: a closed card flashed
   * and there was nothing on it to read. Registering the expansion by field id reuses the
   * carousel's mechanism, and covers the sub-field ids too, since those are what the diff names
   * first and they live inside the part that is collapsed away.
   */
  useEffect(() => {
    if (!fieldId) return;
    const expand = () => setCollapsed(false);
    const offs = [fieldId, `${fieldId}.content`, `${fieldId}.evaluation`, `${fieldId}.trigger`,
      `${fieldId}.title`].map(id => registerFieldReveal(id, expand));
    return () => offs.forEach(off => off());
  }, [fieldId]);

  const evalDisabled = evalLocked ?? locked;
  const missingContent = isFeedbackContentEmpty(content);
  const sharedToolbar = !!(activeEditorRef && setActiveEditor);

  // Collapsing (or deleting) this card unmounts its RichTextEditor/Tiptap instance entirely —
  // if THIS card's editor was the shared toolbar's current target, nothing else clears that
  // reference, so the toolbar would otherwise keep showing as active/enabled against a
  // destroyed editor. Cleanup fires on every `collapsed` transition and on unmount (React runs
  // an effect's cleanup in both cases), and only acts when this card was actually the one
  // registered — never clears some other card's active editor.
  useEffect(() => {
    if (!sharedToolbar) return;
    return () => {
      if (activeEditorRef!.current === editorRef.current) {
        activeEditorRef!.current = null;
        setActiveEditor!(null);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  const labelChipColor =
    chipToneClass(evaluation);
  // Matches the eval cycler button's own icon (evalBtnContent below) — every other chip style
  // in this file (custom triggerZone content built per-block) already pairs its color with a
  // Check/X/Minus icon, so the generic lockedTriggerLabel chip (e.g. No Answer) shouldn't be
  // the one style that's just colored text with no icon.
  const labelChipIcon =
    evaluation === 'correct'   ? <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} /> :
    evaluation === 'incorrect' ? <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} /> :
    <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />;

  const evalBtnSharedClass = `flex w-9 shrink-0 items-center justify-center rounded-md border transition-colors ${evalDisabled ? 'cursor-default opacity-40' : ''} ${
    // dark:border/text-ws-text-link — ws-primary is a dark, low-luminance blue in dark
    // mode, so at opacity-40 (applied when locked/disabled below) it's nearly invisible;
    // same fix already used for this exact contrast issue elsewhere in this file.
    evaluation === 'correct'   ? 'border-ws-primary dark:border-ws-text-link bg-ws-primary/10 text-ws-primary dark:text-ws-text-link' :
    evaluation === 'incorrect' ? 'border-ws-error-fg bg-ws-error-bg/40 text-ws-error-fg' :
    // border/text-ws-text rather than border-ws-border-strong/text-ws-muted — an active
    // neutral state needs to read as clearly "on," not just a shade lighter than a
    // genuinely disabled/locked button (which dims everything via opacity-40 on top of
    // whichever color applies here). Border matches the icon color, same as correct/incorrect.
    // The hover is the AFFORDANCE, so it goes when the control does. A locked neutral cycler was
    // still turning primary under the pointer, which reads as "press me" on a button that has no
    // onClick — and in survey/optional every targeted cycler is locked.
    `border-ws-text bg-ws-btn text-ws-text${evalDisabled ? '' : ' hover:border-ws-primary hover:text-ws-primary'}`
  }`;
  const evalBtnContent = (
    <>
      {evaluation === 'correct'   && <Check aria-hidden='true' className='h-4 w-4' strokeWidth={3} />}
      {evaluation === 'incorrect' && <X aria-hidden='true' className='h-4 w-4' strokeWidth={3} />}
      {evaluation === 'neutral'   && <Minus aria-hidden='true' className='h-4 w-4' strokeWidth={2} />}
    </>
  );
  const evalStateLabel = evaluation === 'correct' ? t('blocks.feedbackCard.evalCorrect') :
    evaluation === 'incorrect' ? t('blocks.feedbackCard.evalIncorrect') : t('blocks.feedbackCard.evalNeutral');
  const evalBtnProps = {
    type: 'button' as const,
    'data-field-id': fieldId && `${fieldId}.evaluation`,
    onClick: evalDisabled ? undefined : onEvaluationCycle,
    'aria-label': evalDisabled
      ? t('blocks.feedbackCard.evalStateAriaLabel', { state: evalStateLabel })
      : t('blocks.feedbackCard.evalCycleAriaLabel', { state: evalStateLabel }),
    'aria-disabled': evalDisabled ? ('true' as const) : undefined,
  };
  const collapsedEvalButton = (
    <button {...evalBtnProps} className={`self-stretch min-h-9 ${evalBtnSharedClass}`}>
      {evalBtnContent}
    </button>
  );

  useEffect(() => {
    if (titleRef.current) titleRef.current.textContent = title;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!collapsed && titleRef.current) titleRef.current.textContent = lastTitle.current;
  }, [collapsed]);

  function handleTitleBlur() {
    const text = titleRef.current?.textContent?.trim() ?? '';
    setTitleEmpty(!text);
    if (!text) {
      if (titleRef.current) titleRef.current.textContent = lastTitle.current;
      setTitleEmpty(!lastTitle.current);
      return;
    }
    if (text !== lastTitle.current) {
      lastTitle.current = text;
      onTitleChange(text);
    }
  }

  function handleTitleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    if (e.key === 'Escape') {
      if (titleRef.current) titleRef.current.textContent = lastTitle.current;
      e.currentTarget.blur();
    }
  }

  function handleTitlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain').replace(/\n/g, ' ');
    document.execCommand('insertText', false, text);
  }

  // Two fixed-size slots (the duplicate/warning alert, then missing-content) always reserve their
  // space whether or not they're actually showing a warning right now — otherwise a wide chip has
  // nothing stopping it from crowding right up against a warning icon the instant one appears.
  //
  // Declared OUTSIDE the collapsed branch and rendered in BOTH states. They used to exist only when
  // collapsed, which did two bad things: a warning silently vanished the moment you opened the card
  // to act on it, and the chip row gained their width on expand — so a wrapped set of chips reflowed
  // to fewer lines and visibly respread (MCQ multi-correct went 3 lines closed, 2 lines open).
  // `AlertTriangle`, not `CircleAlert`. Every one of these is severity WARNING, and the app already
  // has a settled shape for that: `IssueRow` draws a warning as a triangle and an error as an
  // octagon, everywhere else in the editor. This card had been speaking its own dialect — a circle
  // for the same fact the bar four lines below it was drawing as a triangle — so a teacher had no
  // way to read severity off the shape. Colour was already right; only the shape disagreed.
  const alertIcon = error
    ? <><OctagonAlert aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-error-fg' /><span className='sr-only'>{error}</span></>
    : warning
    ? <><AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-warning-fg' /><span className='sr-only'>{warning}</span></>
    : null;
  const missingIcon = missingContent
    ? <><MessageSquareOff aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-warning-fg' /><span className='sr-only'>{t('blocks.feedbackCard.missingContentWarning')}</span></>
    : null;
  /**
   * The warning icons, for the COLLAPSED card only.
   *
   * A closed card has no bottom bar, so the icon is the only thing carrying the warning and it has
   * to be there. An open card states each warning in words at the bottom, with the same icon beside
   * it, so repeating it up here said the same thing twice — and said it in the one place with no
   * room, jammed against the end of the condition editor.
   *
   * The open card used to keep these boxes and drop only their contents, so a chip could not shift
   * when the card toggled. That reserve is gone: an open card shows its trigger EDITOR where a
   * closed one shows a chip, so the two were never the same width for any block with a custom
   * triggerZone, and holding 36px empty to stabilise a jump that happened anyway was a straight
   * loss — 36px off the widest thing on the card, against a condition field that now has to hold
   * equations.
   *
   * A slot with no icon to ever hold is still not rendered at all — a dead 14px gap on every card
   * is not worth avoiding a reflow in the one moment a warning arrives.
   */
  const statusIcons = (
    <>
      {alertIcon && (
        <span className='flex h-3.5 w-3.5 shrink-0 items-center justify-center'>{alertIcon}</span>
      )}
      {missingIcon && (
        <span className='flex h-3.5 w-3.5 shrink-0 items-center justify-center'>{missingIcon}</span>
      )}
    </>
  );

  // ── Collapsed ──────────────────────────────────────────────────────────────
  if (collapsed) {
    // Mechanically-derived titles (locked/titleLocked — true for every real card in the app
    // today) render no title text at all, since it's redundant with the trigger chip; in that
    // case the chips and the warning/chevron icons share a single row instead of the title
    // owning its own row above an otherwise-empty one, so the card actually collapses to one
    // line (unless the chips themselves wrap). The free-text path (title text shown) keeps its
    // own title row above the chip row, since there's real text that needs the space.
    const hideTitle = locked || titleLocked;
    const chipRow = (
      // min-h-9 + items-center, same as the expanded trigger zone: the ROW is items-start (so the
      // chevron stays pinned top-right) while a single-line chip still centres against the eval
      // cycler's own 36px. Without this the chip sat 6px high when collapsed.
      <div className='flex min-h-9 min-w-0 flex-1 flex-wrap items-center gap-1'>
        {triggerZoneSummary ?? (lockedTriggerLabel && (
          <span className={`${TRIGGER_CHIP_CLASS} ${labelChipColor}`}>
            {labelChipIcon}
            <ChipDivider />
            <span className={TRIGGER_CHIP_TEXT_CLASS}>{lockedTriggerLabel}</span>
          </span>
        ))}
      </div>
    );
    return (
      <div data-role='feedback-card' data-field-id={fieldId} className={`flex gap-2 rounded-xl border p-3 transition-colors ${
        disabled ? 'pointer-events-none opacity-40 border-ws-border' :
        error ? 'border-ws-error-fg' :
        warning ? 'border-ws-warning-fg' : 'border-ws-border'
      }`}>
        {collapsedEvalButton}
        <button
          type='button'
          onClick={() => setCollapsed(false)}
          aria-expanded={false}
          className={`flex min-w-0 flex-1 text-left ${hideTitle ? 'items-start gap-2' : 'flex-col gap-1.5'}`}
        >
          {hideTitle ? (
            <>
              {chipRow}
              {/* Mirrors the expanded row's furniture EXACTLY — same gap-2, same pt-2 icon slots, and
                  a chevron box the same 32px (h-8 w-8 -mx-1) as the expanded collapse BUTTON. Anything
                  narrower here and the chip zone is wider closed than open, which is what made a
                  wrapped chip set respread on every toggle. A span, not a button: the whole collapsed
                  card is already one button and nesting is invalid HTML. items-start keeps the chevron
                  pinned top-right in both states so it never shifts under the cursor. */}
              <div className='flex shrink-0 items-start gap-1 pt-2'>
                {statusIcons}
              </div>
              <span className='-mx-1 flex h-8 w-8 shrink-0 items-center justify-center text-ws-muted'>
                <ChevronRight aria-hidden='true' className='h-4 w-4 shrink-0' />
              </span>
            </>
          ) : (
            <>
              <div className='flex min-w-0 w-full items-center gap-1'>
                <span className={`min-w-0 flex-1 truncate text-sm ${inactive ? 'italic text-ws-muted' : 'text-ws-text'}`}>{title}</span>
                <div className='flex-1' />
                {statusIcons}
                <ChevronRight aria-hidden='true' className='h-4 w-4 shrink-0 text-ws-muted' />
              </div>
              {/* Same container classes as the other two trigger zones (the collapsed hideTitle
                  row and the expanded row). It was missing `min-w-0 flex-1`, so a chip measured
                  against a different available width here than when the card opened, and chips
                  visibly resized on expand/collapse. */}
              <div className='flex min-w-0 flex-1 flex-wrap items-center gap-1'>
                {triggerZoneSummary ?? (lockedTriggerLabel && (
                  <span className={`${TRIGGER_CHIP_CLASS} ${labelChipColor}`}>
                    {labelChipIcon}
                    <ChipDivider />
                    <span className={TRIGGER_CHIP_TEXT_CLASS}>{lockedTriggerLabel}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </button>
      </div>
    );
  }

  // ── Expanded ───────────────────────────────────────────────────────────────
  return (
    <div data-role='feedback-card' data-field-id={fieldId} className={`flex flex-col gap-3 rounded-xl border p-3 transition-colors ${
      disabled ? 'pointer-events-none opacity-40 border-ws-border' :
      armed ? 'border-ws-warning-fg bg-ws-warning-bg/40' :
      error ? 'border-ws-error-fg' :
      warning ? 'border-ws-warning-fg' : 'border-ws-border'
    }`}>
      {/* Same padding (p-3) and row gap (gap-2) as the collapsed card, so the eval button
          and trigger chip land at the exact same x/y position in both states. */}
      <div className='flex gap-2'>
        {collapsedEvalButton}
        {(() => {
          const hideTitle = locked || titleLocked;
          const collapseButton = (
            <button
              type='button'
              aria-label={t('blocks.feedbackCard.collapseCard')}
              aria-expanded={true}
              onClick={() => setCollapsed(true)}
              className='-mx-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ws-muted transition-colors hover:bg-ws-hover hover:text-ws-text'
            >
              <ChevronDown className='h-4 w-4' />
            </button>
          );
          /*
           * The condition, filling the space between the eval cycler and the collapse chevron.
           *
           * Two layouts, because a chip and an editor want opposite things from this slot. A CHIP is
           * sized by its text and may wrap to a second line, so it keeps the wrapping row, centred
           * against the cycler (min-h-9 matches the cycler's own `self-stretch min-h-9`; without it
           * a short chip pinned to the top of a 36px row here while the collapsed card centred it,
           * and visibly jumped on expand).
           *
           * An EDITOR should fill the slot. It did not: the wrapping row sizes children to their
           * content, so numerical's trigger editor — whose root has no width of its own — sat at
           * ~160px inside a 210px slot, while compass's (which does set `w-full`) filled it. A flex
           * COLUMN stretches its children by default, so this is one rule here rather than a
           * `w-full` each of eleven blocks has to remember.
           */
          const triggerZoneNode = triggerZone ? (
            /**
             * Tagged, because the trigger is a FIELD an undo can name.
             *
             * `changedFields` reports `<stateId>.trigger` when a teacher changes what a feedback
             * fires on — a number, a range, a unit — and this component already registers a reveal
             * for that id so the card expands to show it. Nothing carried the tag, though, so the
             * reveal expanded the card and then rang the card: editing the MESSAGE highlighted the
             * message, editing the NUMBER beside it highlighted the whole box. Reported from the
             * product against numerical; it was every block, since they all share this component
             * and each passes its own `triggerZone`.
             */
            <div data-field-id={fieldId && `${fieldId}.trigger`}
              className='flex min-h-9 min-w-0 flex-1 flex-col justify-center gap-1'>
              {triggerZone}
            </div>
          ) : (
            <div className='flex min-h-9 min-w-0 flex-1 flex-wrap items-center gap-1'>
              {lockedTriggerLabel && (
                <span className={`${TRIGGER_CHIP_CLASS} ${labelChipColor}`}>
                  {labelChipIcon}
                  <ChipDivider />
                  <span className={TRIGGER_CHIP_TEXT_CLASS}>{lockedTriggerLabel}</span>
                </span>
              )}
            </div>
          );
          // Mechanically-derived titles (locked/titleLocked, true for every real card today)
          // render no title text at all, since it's redundant with the trigger zone — in that
          // case the trigger chips and the collapse chevron share a single row instead of the
          // title owning its own row above an otherwise-empty one, matching the same fix
          // applied to the collapsed card above. The free-text path keeps its own title row.
          if (hideTitle) {
            // items-start, not items-center — every other block's triggerZone is a single-line
            // chip/dropdown, where the two alignments look identical, but Ordering's custom-order
            // trigger renders a full multi-row draggable item list here (OrderingCustomOrderEditor),
            // and items-center was pulling the small collapse chevron down to vertical-center
            // against that tall content instead of staying pinned top-right where a collapse
            // control belongs.
            return (
              <div className='flex min-w-0 flex-1 items-start gap-2'>
                {triggerZoneNode}
                {/* No reserved warning slots — see `statusIcons`. They were held open here, empty,
                    to stop the chip row respreading on toggle, but an open card shows its trigger
                    EDITOR in this row rather than the closed card's chip, so the widths never
                    matched anyway and the reserve only narrowed the condition field. */}
                {collapseButton}
              </div>
            );
          }
          return (
            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
              <div className='flex items-center gap-2'>
                <div className='relative min-w-0 flex-1'>
                  {titleEmpty && (
                    <span className='pointer-events-none absolute inset-0 select-none px-1 py-0.5 text-sm font-medium text-ws-muted'>
                      {t('blocks.feedbackCard.feedbackTitlePlaceholder')}
                    </span>
                  )}
                  <div
                    ref={titleRef}
                    contentEditable
                    suppressContentEditableWarning
                    role='textbox'
                    aria-label={t('blocks.feedbackCard.feedbackTitleLabel')}
                    aria-placeholder={t('blocks.feedbackCard.feedbackTitlePlaceholder')}
                    onBlur={handleTitleBlur}
                    onKeyDown={handleTitleKeyDown}
                    onPaste={handleTitlePaste}
                    onInput={() => setTitleEmpty(!titleRef.current?.textContent?.trim())}
                    // No focus border — text entry shows focus by its caret (see globals.css).
                    className='truncate rounded border-b border-transparent px-2 py-1 text-sm font-medium text-ws-text ws-inline-input focus:outline-none'
                  />
                </div>
                {collapseButton}
              </div>
              {/* Trigger zone — same wrapping-row layout as the collapsed triggerZoneSummary,
                  so a chip list reads identically expanded or collapsed, no forced line breaks. */}
              {triggerZoneNode}
            </div>
          );
        })()}
      </div>

      {/* Editor — toolbar appears on focus and hides when focus leaves, unless a shared
          externally-rendered toolbar was supplied (see the activeEditorRef/setActiveEditor
          doc comment above), in which case this card's own toolbar stays hidden entirely and
          it just registers itself as the active editor on focus instead. */}
      <div
        onClick={() => editorRef.current?.commands.focus()}
        {...(sharedToolbar ? {} : {
          onFocus: () => setToolbarActive(true),
          onBlur: (e: React.FocusEvent<HTMLDivElement>) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setToolbarActive(false); },
        })}
      >
        <RichTextEditor
          // The MESSAGE, not the card. `changedFields` names `<stateId>.content` before the state
          // itself, so an undo of an edit to this text points here rather than ringing the whole
          // card — which, collapsed, shows only a title and no editable field at all.
          fieldId={fieldId && `${fieldId}.content`}
          content={content}
          onChange={onContentChange}
          // Deliberately no `placeholder` — blank feedback text is a valid, unmarked state, never
          // a "please fill this in" prompt: a default slot's own seed text was always real
          // content, not a placeholder, so if a teacher deletes it the field should just stay
          // blank; a targeted state starts blank by design and shouldn't look unfinished either.
          // Applies uniformly across every block, since they all share this one component.
          editorClassName='min-h-[60px]'
          hideToolbar={sharedToolbar}
          toolbarVisible={sharedToolbar ? undefined : toolbarActive}
          onEditorReady={(e) => { editorRef.current = e; }}
          onEditorFocus={sharedToolbar ? () => {
            if (editorRef.current) { activeEditorRef!.current = editorRef.current; setActiveEditor!(editorRef.current); }
          } : undefined}
        />
      </div>

      {/* Bottom bar: warnings left, delete right */}
      {(!locked || warning || error || missingContent) && (
        <div className='flex items-center gap-2'>
          <div className='flex flex-1 flex-wrap items-center gap-3'>
            {error && (
              <span className='flex items-center gap-1 text-xs text-ws-error-fg'>
                <OctagonAlert aria-hidden='true' className='h-3 w-3 shrink-0' />
                {error}
              </span>
            )}
            {warning && (
              <span className='flex items-center gap-1 text-xs text-ws-warning-fg'>
                <AlertTriangle aria-hidden='true' className='h-3 w-3 shrink-0' />
                {warning}
              </span>
            )}
            {missingContent && (
              <span className='flex items-center gap-1 text-xs text-ws-warning-fg'>
                <MessageSquareOff aria-hidden='true' className='h-3 w-3 shrink-0' />
                {t('blocks.feedbackCard.missingContentWarning')}
              </span>
            )}
          </div>
          {!locked && (
            <button
              type='button'
              aria-label={armed ? t('blocks.feedbackCard.feedbackDeleteConfirm') : t('blocks.feedbackCard.feedbackDelete')}
              onClick={(e) => {
                if (!armed) { setArmed(true); return; }
                // This card (and its own delete button) unmounts once onDelete() removes it from
                // the caller's list — with nothing done here, keyboard/screen-reader focus would
                // otherwise fall all the way back to <body>. This card has no direct knowledge of
                // its siblings (it's just one item a caller maps over), so the nearest sibling
                // card is found generically via `data-role` DOM traversal instead of requiring
                // every caller to thread a focus-management prop through just for this.
                focusSiblingFeedbackCardAfterRemoval(e.currentTarget);
                onDelete();
              }}
              onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armed) setArmed(false); }}
              onBlur={() => { if (armed) setArmed(false); }}
              onKeyDown={(e) => { if (e.key === 'Escape' && armed) { e.stopPropagation(); setArmed(false); } }}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors ${
                armed ? 'bg-ws-warning-bg text-ws-warning-fg' : 'text-ws-muted hover:bg-ws-warning-bg hover:text-ws-warning-fg'
              }`}
            >
              <Trash2 className='h-4 w-4' />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
